import React from "react";

import {
	AvatarGroup,
	Chip,
	type ICellRendererParams,
	Link,
	type PYColumnDef,
	type SortChangedEvent,
	Table,
	Text,
} from "@performyard/py-components";
import { MeetingExtendedModelJson } from "@performyard/service-contracts";

import { getFormattedMeetingDateString } from "../utils";

interface CompanyMeetingsTableProps {
	meetings: MeetingExtendedModelJson[];
	isFetchingMeetings: boolean;
	activeSort: string;
	activeSortDirection: string;
	handleColumnHeaderClick: (column: { name: string; direction: "asc" | "desc" }) => void;
}

const NameCellRenderer = (params: ICellRendererParams<MeetingExtendedModelJson>) => {
	const meeting = params.data;
	if (!meeting) return null;
	return (
		<div className="flex h-full items-center">
			<Link
				href={`/meeting-workspaces/${meeting.meetingWorkspaceId}/meetings/${meeting._id}`}
				forceHardReload
				underline="hover"
			>
				<Text size="paragraph-3" weight="regular" dangerouslySetClassName="py-line-clamp-1 max-w-[250px]">
					{meeting.name}
				</Text>
			</Link>
		</div>
	);
};

const ParticipantsCellRenderer = (params: ICellRendererParams<MeetingExtendedModelJson>) => {
	const meeting = params.data;
	if (!meeting) return null;
	const employees =
		meeting.participants?.map((participant) => ({
			_id: participant.userId,
			name: participant.name,
		})) ?? [];
	return (
		<div className="flex h-full items-center">
			<AvatarGroup
				employees={employees}
				maxLength={employees.length <= 5 ? 5 : 4}
				employeeNameTooltip={true}
				hasLinks={true}
				modalTitle="Workspace Participants"
				modalSubtitle={meeting.name}
			/>
		</div>
	);
};

const WorkspaceNameCellRenderer = (params: ICellRendererParams<MeetingExtendedModelJson>) => {
	const meeting = params.data;
	if (!meeting) return null;
	return (
		<div className="flex h-full items-center">
			<Link href={`/meeting-workspaces/${meeting.meetingWorkspaceId}`} forceHardReload underline="hover">
				<Text size="paragraph-3" weight="regular" dangerouslySetClassName="py-line-clamp-1 max-w-[250px]">
					{meeting.meetingWorkspaceName || ""}
				</Text>
			</Link>
		</div>
	);
};

const MeetingDateCellRenderer = (params: ICellRendererParams<MeetingExtendedModelJson>) => {
	const meeting = params.data;
	if (!meeting) return null;
	return (
		<div className="flex h-full items-center">
			<Text size="paragraph-3" weight="regular" dangerouslySetClassName="py-line-clamp-1 max-w-[250px]">
				{getFormattedMeetingDateString(meeting.startDateTime, meeting.endDateTime)}
			</Text>
		</div>
	);
};

const StatusCellRenderer = (params: ICellRendererParams<MeetingExtendedModelJson>) => {
	const meeting = params.data;
	if (!meeting) return null;
	switch (meeting.status) {
		case "active":
			return <Chip label="Active" color="primary" size={20} />;
		case "archived":
			return <Chip label="Archived" color="neutral" size={20} />;
		case "completed":
			return <Chip label="Completed" color="green" size={20} />;
		case "canceled":
			return <Chip label="Canceled" color="red" size={20} />;
		default:
			return <Chip label={meeting.status} color="primary" size={20} />;
	}
};

const columnDefs: PYColumnDef<MeetingExtendedModelJson>[] = [
	{
		field: "name",
		headerName: "Meeting Name",
		sortable: true,
		resizable: false,
		width: 288,
		cellRenderer: NameCellRenderer,
	},
	{
		field: "participants",
		headerName: "Participants",
		sortable: false,
		resizable: false,
		width: 106,
		cellRenderer: ParticipantsCellRenderer,
	},
	{
		field: "meetingWorkspaceName",
		headerName: "Meeting Workspace Name",
		sortable: true,
		resizable: false,
		width: 288,
		cellRenderer: WorkspaceNameCellRenderer,
	},
	{
		field: "meetingDate",
		headerName: "Meeting Date",
		sortable: true,
		resizable: false,
		width: 272,
		cellRenderer: MeetingDateCellRenderer,
	},
	{
		field: "status",
		headerName: "Status",
		sortable: true,
		resizable: false,
		width: 140,
		cellRenderer: StatusCellRenderer,
	},
];

const CompanyMeetingsTable = (props: CompanyMeetingsTableProps) => {
	const { meetings, isFetchingMeetings, activeSort, activeSortDirection, handleColumnHeaderClick } = props;

	const onSortChanged = (event: SortChangedEvent<MeetingExtendedModelJson>) => {
		const columnState = event.api.getColumnState();
		const sortedColumn = columnState.find((col) => col.sort);
		if (sortedColumn?.colId) {
			handleColumnHeaderClick({
				name: sortedColumn.colId,
				direction: sortedColumn.sort as "asc" | "desc",
			});
		}
	};

	return (
		<div data-testid="companyMeetingsContainer">
			<Table<MeetingExtendedModelJson>
				autoHeight
				rowData={meetings}
				columnDefs={columnDefs}
				loading={isFetchingMeetings}
				getRowId={(params) => params.data._id}
				onSortChanged={onSortChanged}
				emptyStateText="No Meetings Found"
				initialState={{
					sort: {
						sortModel: activeSort
							? [{ colId: activeSort, sort: activeSortDirection as "asc" | "desc" }]
							: [],
					},
				}}
				animateRows={false}
			/>
		</div>
	);
};

export default CompanyMeetingsTable;
